import React, { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const split = (s) => s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);

export const PoEmailDialog = ({ api, open, onOpenChange, po, vendor, poEmail, onSent }) => {
  const [form, setForm] = useState({ to: '', cc: '', bcc: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !po) return;
    setForm({
      to: po.vendor_contact_email || vendor?.contact_email || '', cc: '', bcc: '',
      subject: `Purchase Order ${po.po_number}`,
      message: `Hello${(po.vendor_contact_name || vendor?.contact_name) ? ` ${(po.vendor_contact_name || vendor.contact_name).split(' ')[0]}` : ''},\n\nPlease find attached Purchase Order ${po.po_number}. Kindly confirm receipt, pricing and expected ship date.\n\nThank you,\nAccounts Payable${poEmail ? `\n${poEmail}` : ''}`,
    });
  }, [open, po, vendor, poEmail]);

  const send = async () => {
    const to = split(form.to);
    if (!to.length) { toast.error('Add the vendor email'); return; }
    setBusy(true);
    try {
      const r = await api().post(`/purchase-orders/${po.id}/email`, { to, cc: split(form.cc), bcc: split(form.bcc), subject: form.subject, message: form.message });
      toast.success(`Sent to ${to.join(', ')}${r.data.fallback ? ` (from test sender, replies go to ${poEmail})` : ''}${r.data.issued ? ' · PO issued' : ''}`);
      onSent(r.data.po);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
    finally { setBusy(false); }
  };

  const willIssue = po && ['Draft', 'Rejected'].includes(po.status);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="po-email-dialog">
        <DialogHeader><DialogTitle>Email {po?.po_number} to Vendor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>To *</Label><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="vendor@example.com" className="rounded-sm" data-testid="po-email-to" /></div>
          <div><Label>CC</Label><Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} placeholder="comma separated" className="rounded-sm" data-testid="po-email-cc" /></div>
          <div><Label>BCC</Label><Input value={form.bcc} onChange={(e) => setForm({ ...form, bcc: e.target.value })} placeholder="comma separated" className="rounded-sm" data-testid="po-email-bcc" /></div>
          {(() => {
            const known = [...new Set([po?.vendor_contact_email, vendor?.contact_email, ...((po?.emails || []).flatMap(e => [...e.to, ...(e.cc || [])]))].filter(Boolean))].filter(x => !split(form.to).includes(x) && !split(form.cc).includes(x));
            return known.length > 0 && <div className="flex gap-1 flex-wrap text-[11px]" data-testid="po-email-suggestions"><span className="text-slate-500">Add:</span>{known.map(x => <button key={x} type="button" onClick={() => setForm({ ...form, to: [...split(form.to), x].join(', ') })} className="px-1.5 py-0.5 border border-slate-200 rounded-sm hover:bg-slate-50" data-testid={`po-email-suggest-${x}`}>{x}</button>)}</div>;
          })()}
          <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm" data-testid="po-email-subject" /></div>
          <div><Label>Message</Label><Textarea rows={7} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm text-sm" data-testid="po-email-message" /></div>
          <div className="text-[11px] text-slate-500 space-y-0.5">
            <div>The branded PO is attached as <span className="mono">{po?.po_number}.pdf</span>. Sent from <b>{poEmail || 'the system sender'}</b> once that domain is verified in Resend; until then it goes from the test sender with replies directed to {poEmail}.</div>
            {poEmail && <div data-testid="po-email-ap-note">A copy goes to <b>{poEmail}</b> automatically (BCC), and AP is emailed when the vendor confirms or reports a problem.</div>}
            {willIssue && <div className="text-blue-700" data-testid="po-email-will-issue">This Draft PO will be marked <b>Issued</b> when sent.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="po-email-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-email-send"><Send className="w-3.5 h-3.5 mr-1" />{busy ? 'Sending…' : 'Send'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PoEmailDialog;
